import type { CSSProperties } from "react";
import { cx } from "../utils/format";

/** Grey, still blocks where content will be while a screen loads (polish round two, the founder, 2026-10-16): no spinner, no blank page, no
 *  "Loading…". Nothing moves, so the motion rule stays whole. Every block is hidden from screen readers; the screen around them says once that
 *  it is busy (`aria-busy` on its region). Four shapes: a `line` of text, a `block` (a chart, a picture, a tile's body), a `row` (a list row),
 *  a `card` (a whole card). `width` / `height` size one block when its place needs it (a short title, a tall chart). */
export type SkeletonShape = "line" | "block" | "row" | "card";
export function Skeleton({ shape = "line", width, height, className }: { shape?: SkeletonShape; width?: string | number; height?: string | number; className?: string }) {
  const style: CSSProperties | undefined = width !== undefined || height !== undefined ? { width, height } : undefined;
  return <span className={cx("ob-skeleton", `ob-skeleton--${shape}`, className)} style={style} aria-hidden="true" />;
}

/** A few lines of text, the last one shorter, as a paragraph or a card's body reads. */
export function SkeletonLines({ lines = 3, className }: { lines?: number; className?: string }) {
  return <span className={cx("ob-skeleton-lines", className)} aria-hidden="true">{Array.from({ length: lines }, (_, i) => <Skeleton key={i} shape="line" width={i === lines - 1 && lines > 1 ? "60%" : undefined} />)}</span>;
}

/** A list of rows (the inbox list, a card's list): `rows` row blocks. */
export function SkeletonList({ rows = 6, className }: { rows?: number; className?: string }) {
  return <div className={cx("ob-skeleton-list", className)} aria-hidden="true">{Array.from({ length: rows }, (_, i) => <Skeleton key={i} shape="row" />)}</div>;
}

/** A table's loading state with the table's own column headers, so nothing jumps when the rows arrive. `className` is the loaded table's
 *  class (`ob-dtable`, …) so the frame is the same. The headers stay readable (they are the table's shape, not content). */
export function SkeletonTable({ columns, rows = 6, className }: { columns: string[]; rows?: number; className?: string }) {
  return (
    <table className={cx("ob-skeleton-table", className)}>
      <thead><tr>{columns.map((c, i) => <th key={i} scope="col">{c}</th>)}</tr></thead>
      <tbody>{Array.from({ length: rows }, (_, r) => <tr key={r}>{columns.map((_, i) => <td key={i}><Skeleton shape="line" width={i === 0 ? "70%" : "50%"} /></td>)}</tr>)}</tbody>
    </table>
  );
}
